import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { CheckCircle, AlertTriangle, HelpCircle, FileSearch, RefreshCw } from 'lucide-react';

const STATUS_STYLES = {
  aligned: {
    badge: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle,
    iconClass: 'text-green-600',
    panel: 'border-green-200 bg-green-50/60',
  },
  admin_accepted: {
    badge: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle,
    iconClass: 'text-green-600',
    panel: 'border-green-200 bg-green-50/60',
  },
  minor_differences: {
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: HelpCircle,
    iconClass: 'text-blue-600',
    panel: 'border-slate-200 bg-slate-50/80',
  },
  action_required: {
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: AlertTriangle,
    iconClass: 'text-amber-600',
    panel: 'border-amber-200 bg-amber-50/60',
  },
  cannot_assess: {
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: HelpCircle,
    iconClass: 'text-slate-500',
    panel: 'border-slate-200 bg-slate-50/80',
  },
};

export function resolveCvAlignmentDisplay(alignment, context = {}) {
  if (!alignment) return null;

  const employee = context.employee || {};
  const mismatch = context.employmentMismatch || {};
  const latestReviewNote = [...(mismatch.employment_mismatch_review_notes || [])]
    .reverse()
    .find((entry) => entry?.note)?.note;

  if (alignment.display_status === 'admin_accepted') {
    const note = alignment.admin_resolution?.note || mismatch.cv_approval_note || employee.cv_approval_note || latestReviewNote;
    return {
      status: 'admin_accepted',
      statusLabel: alignment.display_status_label || 'Reviewed & accepted',
      summary: alignment.display_summary || 'This CV has been checked for recruitment.',
      note,
      resolved: true,
      originalStatus: alignment.status,
    };
  }

  const cvApproved = String(mismatch.cv_status || employee.cv_status || '').toLowerCase() === 'approved';
  const reviewed = Boolean(mismatch.employment_mismatch_reviewed || employee.employment_mismatch_reviewed);
  const note = mismatch.cv_approval_note || employee.cv_approval_note || latestReviewNote;

  if (cvApproved || reviewed) {
    return {
      status: 'admin_accepted',
      statusLabel: cvApproved ? 'Reviewed & approved' : 'Reviewed & accepted',
      summary: note
        ? `This CV has been checked for recruitment. Note: ${note}`
        : 'This CV has been checked for recruitment.',
      note,
      resolved: true,
      originalStatus: alignment.status,
    };
  }

  return {
    status: alignment.status,
    statusLabel: alignment.status_label || alignment.status,
    summary: alignment.status === 'action_required'
      ? 'Check the CV against the official work history before approving this section.'
      : alignment.summary,
    note: null,
    resolved: false,
    originalStatus: alignment.status,
  };
}

export default function CvEmploymentAlignmentPanel({
  alignment,
  employee = null,
  employmentMismatch = null,
  onReviewCv,
  onViewMismatch,
  onRequestReplacement,
  reviewLoading = false,
  compact = false,
}) {
  if (!alignment) {
    const cvApproved = String(employee?.cv_status || '').toLowerCase() === 'approved';
    return (
      <div className={`rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 ${compact ? '' : 'mt-3'}`}>
        <p className="text-xs text-slate-600">
          {cvApproved
            ? 'This CV was accepted during review.'
            : <>Check the CV against the official work history before sign-off.</>}
        </p>
        {onReviewCv && !cvApproved && (
          <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={onReviewCv} disabled={reviewLoading}>
            <FileSearch className="h-3 w-3 mr-1" />
            Review CV
          </Button>
        )}
      </div>
    );
  }

  const display = resolveCvAlignmentDisplay(alignment, { employee, employmentMismatch });
  const style = STATUS_STYLES[display.status] || STATUS_STYLES.cannot_assess;
  const Icon = style.icon;
  const structPct = alignment.structured_coverage?.accounted_percent ?? alignment.structured_coverage?.coverage_percent;
  const cvPct = alignment.cv_coverage?.accounted_percent ?? alignment.cv_coverage?.coverage_percent;

  return (
    <div
      className={`rounded-lg border p-3 ${style.panel} ${compact ? '' : 'mt-3'}`}
      data-testid="cv-employment-alignment-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${style.iconClass}`} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900">
              {display.resolved ? 'CV check complete' : 'CV check needs review'}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">{display.summary}</p>
          </div>
        </div>
        <Badge variant="outline" className={`text-xs flex-shrink-0 ${style.badge}`}>
          {display.statusLabel}
        </Badge>
      </div>

      {display.resolved && display.note && (
        <p className="mt-2 text-xs text-green-800 rounded border border-green-200 bg-white/70 px-2 py-1.5">
          <span className="font-medium">Admin note:</span> {display.note}
        </p>
      )}

      {(structPct != null || cvPct != null) && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-600">
          <div className="rounded border border-white/80 bg-white/70 px-2 py-1">
            <span className="text-slate-500">Official work history</span>
            <p className="font-medium text-slate-800">
              {alignment.structured_coverage?.meets_10_year_requirement ? 'Complete' : `${Math.round(structPct || 0)}% covered`}
            </p>
          </div>
          <div className="rounded border border-white/80 bg-white/70 px-2 py-1">
            <span className="text-slate-500">CV evidence</span>
            <p className="font-medium text-slate-800">
              {alignment.cv_coverage?.meets_10_year_requirement ? 'Complete' : `${Math.round(cvPct || 0)}% covered`}
            </p>
          </div>
        </div>
      )}

      {!display.resolved && alignment.mismatch_count > 0 && (
        <p className="text-xs text-amber-800 mt-2">
          {alignment.mismatch_count} item{alignment.mismatch_count !== 1 ? 's' : ''} need checking before this CV can be approved.
        </p>
      )}

      {display.resolved && display.originalStatus === 'action_required' && alignment.mismatch_count > 0 && (
        <p className="text-xs text-slate-500 mt-2">
          Original finding: {alignment.mismatch_count} difference{alignment.mismatch_count !== 1 ? 's' : ''} noted - accepted by admin.
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-1.5">
        {onViewMismatch && alignment.mismatch_count > 0 && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onViewMismatch}>
            Check details
          </Button>
        )}
        {onRequestReplacement && !display.resolved && display.status === 'action_required' && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs text-amber-800 border-amber-300 hover:bg-amber-100"
            onClick={onRequestReplacement}
            data-testid="alignment-request-replacement-btn"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Ask for updated CV
          </Button>
        )}
      </div>
    </div>
  );
}
